import { useCallback, useContext, useEffect } from "react";
import { NetworkStatus, useApolloClient } from "@apollo/client";
import { ColumnDef } from "@tanstack/table-core/build/lib/types";
import { useLocation } from "react-router";
import { RESOURCE_NAME } from "common/authorization";
import { PROPERTY_OBJECT_LABEL, TASK_STATUS } from "common/constants";
import { buildLink, INFO_PANEL_TAB_NAME } from "common/routing";
import { QueryDescription, TABLE_NAMES } from "common/utils/queryBuilder";
import {
  GetQueryTableDataQueryVariables,
  GetTaskDocument,
  Maybe,
  useGetQueryTableDataLazyQuery,
  useGetSavedViewsQuery,
  useGetTableConfigQuery,
} from "../../generated/graphql";
import { useLocalTableDisplayConfig } from "../../hooks/useTableDisplayConfig";
import { AuthContext } from "../Authorization/AuthContext";
import { someActionsEnabled } from "../Common/ActionBar/utils";
import { ACTION_COLUMN_DEF_CONSTANTS, ActionCell } from "../Common/ActionCell";
import { ExpandableCell } from "../Common/FullWidthTable/ExpandableCell";
import { FullWidthTable } from "../Common/FullWidthTable/FullWidthTable";
import { Attribute } from "../Common/FullWidthTable/types";
import {
  fieldId,
  initializeColumns,
  mergeFieldGroups,
  MinimalColumnDef,
  sanityCheckLocalColumnOrder,
} from "../Common/FullWidthTable/utils";
import LinkCell from "../Common/LinkCell";
import {
  useManualPaginationConfig,
  useQueryDescription,
} from "../Common/Tables/hooks";
import { buildColumnDefinitions } from "../Common/Tables/utils";
import {
  INITIAL_SUBMISSION_TYPE_REPORTS,
  SDE_DATABASE_REPORT,
} from "../Exports/constants";
import ExportDataButton from "../Exports/ExportDataButton";
import { buildInitialReportTypes } from "../Exports/ExportDataForm";
import { ActionsProps } from "../Inputs/DropdownMenu";
import { Link } from "./__styles__/TasksTable";
import { useEditTaskModal } from "./Form/EditTaskForm";
import { StatusTag } from "./StatusTag";
import { useDeleteTaskWithConfirmation } from "./useDeleteTaskWithConfirmation";

type TaskRowData = {
  "Tasks.id": string;
  "Tasks.name": Maybe<string>;
  "Tasks.status"?: TASK_STATUS;
  "Tasks.assignees"?: Array<{ firstName: string; lastName: string }>;
  "SubmissionTypes.name"?: string;
  "Tasks.relatedSubmissionId": Maybe<string>;
  "Tasks.objectType": Maybe<string>;
  "Tasks.objectId": Maybe<string>;
  "Properties.latitude": Maybe<number>;
  "Properties.longitude": Maybe<number>;
  "Properties.streetAddress": Maybe<string>;
  "CustomMapGeometries.customMapId": Maybe<string>;
};

export const TASKS_TABLE_REQUIRED_FIELDS = [
  { table: TABLE_NAMES.TASKS, name: "id" },
  { table: TABLE_NAMES.TASKS, name: "name" },
  { table: TABLE_NAMES.TASKS, name: "relatedSubmissionId" },
  { table: TABLE_NAMES.TASKS, name: "objectType" },
  { table: TABLE_NAMES.TASKS, name: "objectId" },
  { table: TABLE_NAMES.PROPERTIES, name: "latitude" },
  { table: TABLE_NAMES.PROPERTIES, name: "longitude" },
  { table: TABLE_NAMES.PROPERTIES, name: "streetAddress" },
  { table: TABLE_NAMES.CUSTOM_MAP_GEOMETRIES, name: "customMapId" },
] as const;

type RowWithRequiredFields = {
  original: TaskRowData;
};

const makeCustomCellRenderers = ({
  refetchTable,
  fetchTask,
}: {
  refetchTable: () => void;
  fetchTask: (taskId: string) => void;
}) => ({
  "Tasks.name": {
    cell: ({ row }: { row: RowWithRequiredFields }) => {
      /* eslint-disable-next-line react-hooks/rules-of-hooks -- the cell
       * renderer is a component, it just doesn't start with a capital letter
       */
      const [showTaskModal, hideTaskModal] = useEditTaskModal({
        taskId: row.original["Tasks.id"],
        onSuccess: () => {
          hideTaskModal();
          refetchTable();
        },
      });

      return (
        <Link
          role="button"
          onClick={() => showTaskModal()}
          onMouseOver={() => {
            fetchTask(row.original["Tasks.id"]);
          }}
        >
          {row.original["Tasks.name"]}
        </Link>
      );
    },
  },
  "Tasks.status": {
    cell: ({ row }: { row: RowWithRequiredFields }) => {
      const status = row.original["Tasks.status"];
      return status ? <StatusTag status={status} /> : null;
    },
  },
  "Tasks.assignees": {
    cell: ({ row }: { row: RowWithRequiredFields }) => {
      const assignees = row.original["Tasks.assignees"];

      if (!assignees) {
        return null;
      }

      const tags = assignees.map(
        (assignee: { firstName: string; lastName: string }) => ({
          label: `${assignee.firstName} ${assignee.lastName}`,
        }),
      );

      return <ExpandableCell tags={tags} />;
    },
  },
  "SubmissionTypes.name": {
    cell: ({ row }: { row: RowWithRequiredFields }) => {
      const submissionTypeName = row.original["SubmissionTypes.name"];
      if (!submissionTypeName) {
        return null;
      }

      if (!row.original["Tasks.relatedSubmissionId"]) {
        return <span>{submissionTypeName}</span>;
      }

      const url = buildLink("viewSubmission", {
        submissionId: row.original["Tasks.relatedSubmissionId"],
      });

      return (
        <LinkCell
          href={url}
          target={"_blank"}
          label={submissionTypeName}
          css={{ margin: "0" }}
        />
      );
    },
  },
});

const TaskActions = ({
  rowData,
  onAction,
  fetchTask,
}: {
  rowData: TaskRowData;
  onAction: () => void;
  fetchTask: (taskId: string) => void;
}) => {
  const { authorized, account } = useContext(AuthContext);
  const id = rowData["Tasks.id"];
  const name = rowData["Tasks.name"] || "Untitled Task";

  const deleteTaskWithConfirmation = useDeleteTaskWithConfirmation({
    task: { id, name },
    onDelete: onAction,
  });

  const [showTaskModal, hideTaskModal] = useEditTaskModal({
    taskId: id,
    onSuccess: () => {
      hideTaskModal();
      onAction();
    },
  });

  const canDeleteTask = authorized({
    resource: RESOURCE_NAME.TASK,
    permission: "delete",
  });

  const canUpdateTask = authorized({
    resource: RESOURCE_NAME.TASK,
    permission: "update",
  });

  const isCustomMapGeometry =
    rowData["Tasks.objectType"] !== PROPERTY_OBJECT_LABEL;
  const hasCustomMapData = Boolean(
    rowData["Tasks.objectId"] && rowData["CustomMapGeometries.customMapId"],
  );

  const actions: Array<ActionsProps> = [
    {
      label: "View",
      onClick: () => {
        showTaskModal();
      },
      disabled: !canUpdateTask,
    },
    {
      label: "View on map",
      href: buildLink(
        "map",
        { accountId: account?.id! },
        isCustomMapGeometry && hasCustomMapData ?
          {
            customMapId: rowData["CustomMapGeometries.customMapId"]!,
            geometryId: rowData["Tasks.objectId"]!,
          }
        : {
            lat: rowData["Properties.latitude"]?.toString() ?? "",
            lng: rowData["Properties.longitude"]?.toString() ?? "",
            address: rowData["Properties.streetAddress"] ?? "",
            propertyId: rowData["Tasks.objectId"] ?? "",
            tab: INFO_PANEL_TAB_NAME.TASKS,
          },
      ),
      target: "_blank",
    },
    {
      label: "Delete",
      onClick: () => {
        deleteTaskWithConfirmation();
      },
      variant: "red",
      disabled: !canDeleteTask,
    },
  ];

  return (
    <ActionCell
      actions={actions}
      onMenuDidOpen={() => {
        fetchTask(id);
      }}
    />
  );
};

export const TasksTable = () => {
  const { user, admin, account } = useContext(AuthContext);

  const {
    data: savedViewsResponse,
    loading: loadingSavedViews,
    refetch: refetchSavedViews,
  } = useGetSavedViewsQuery({
    variables: { table: TABLE_NAMES.TASKS },
    fetchPolicy: "cache-and-network",
  });

  const { data: columnConfigResponse, loading: loadingColumnConfig } =
    useGetTableConfigQuery({
      variables: { table: TABLE_NAMES.TASKS },
      fetchPolicy: "network-only",
    });

  const [
    queryTableData,
    { previousData, data: currentData, networkStatus, loading, error, refetch },
  ] = useGetQueryTableDataLazyQuery({
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  const apolloClient = useApolloClient();

  const fetchTask = (taskId: string) => {
    // Using the raw ApolloClient query to fetch the task here because using
    // 'get' from the lazy query hook would imply that the task being fetched
    // "belongs" to this view, which it does not—especially since *which* task
    // is being fetched will be different on every row. The lazy query hook also
    // adds reactive dependencies to the table itself, which is unnecessary;
    // instead, this acts as a 'fire and forget' side effect.
    //
    // It would also be possible to instantiate the lazy query hook in each row,
    // but that would likely add a lot of memory overhead.
    void apolloClient.query({
      query: GetTaskDocument,
      variables: { id: taskId },
    });
  };

  const refetchTable = () => {
    void refetch();
  };

  const actionsColumn: MinimalColumnDef<typeof TASKS_TABLE_REQUIRED_FIELDS> = {
    ...ACTION_COLUMN_DEF_CONSTANTS,
    cell: ({ row }) => {
      const task = row.original;
      return (
        <TaskActions
          rowData={task}
          onAction={refetchTable}
          fetchTask={fetchTask}
        />
      );
    },
  };

  const {
    initialTableState,
    queryDescription,
    updateQueryDescription,
    currentView,
  } = useQueryDescription({
    source: { table: TABLE_NAMES.TASKS },
    defaultSort: [{ id: "Tasks.id", desc: false }],
    savedViews: savedViewsResponse?.account?.savedViews,
  });

  const generatedColumnDefinitions = buildColumnDefinitions({
    columnConfig: columnConfigResponse?.getTableConfig.data || [],
    cellRenderers: makeCustomCellRenderers({ refetchTable, fetchTask }),
  });

  const { pathname } = useLocation();

  const tableId = `${user?.id ?? admin?.id}-${pathname.replace("/", "")}`;
  const { getLocalTableState, setLocalColumnOrder } =
    useLocalTableDisplayConfig({
      tableId,
      defaultValue: {
        columnOrder: [
          ...queryDescription.fields.map(field => fieldId(field)),
          ACTION_COLUMN_DEF_CONSTANTS.id,
        ],
        columnSizing: {},
        sorting: [],
      },
    });

  const manualPaginationConfig = useManualPaginationConfig({
    ...initialTableState.pagination,
    currentTotalPages: currentData?.queryTableData.pageInfo.totalPages,
    previousTotalPages: previousData?.queryTableData.pageInfo.totalPages,
  });

  // remove this any
  const tanstackColumnDefinitions: Array<ColumnDef<any>> = [
    ...generatedColumnDefinitions,
    actionsColumn,
  ];

  const data =
    networkStatus === NetworkStatus.setVariables ? previousData : currentData;

  const search = useCallback(
    ({ page }: { page: number }) => {
      const variables: GetQueryTableDataQueryVariables = {
        description: {
          ...queryDescription,
          fields: mergeFieldGroups(
            queryDescription.fields,
            TASKS_TABLE_REQUIRED_FIELDS,
          ),
        } satisfies QueryDescription,
        page,
      };

      void queryTableData({
        variables,
      });
    },
    [queryDescription, queryTableData],
  );

  useEffect(() => {
    if (queryDescription && !loadingColumnConfig && !loadingSavedViews) {
      search({ page: 1 });
    }
  }, [queryDescription, loadingColumnConfig, loadingSavedViews, search]);

  if (
    loadingColumnConfig ||
    !columnConfigResponse?.getTableConfig.data ||
    loadingSavedViews
  ) {
    return null;
  }

  const loadingDetails = {
    loading,
    loadingText: "Loading tasks",
    noDataText: "No tasks found",
  };

  const localTableConfig = getLocalTableState();

  const { columnOrder: localColumnOrder } = localTableConfig;

  const updatedColumnOrder = sanityCheckLocalColumnOrder({
    localColumnOrder,
    defaultColumnIds: queryDescription.fields.map(field => fieldId(field)),
  });

  if (updatedColumnOrder) {
    setLocalColumnOrder(updatedColumnOrder);
  }

  const actionBarConfig = columnConfigResponse?.getTableConfig.actionBarConfig;
  const enableSelectColumn = someActionsEnabled(actionBarConfig?.actions ?? {});

  const initialColumns = initializeColumns({
    initialTableState,
    tanstackColumnDefinitions,
    initialQueryDescription: queryDescription,
    actionsColumn,
    enableSelectColumn,
  });

  const timeoutError = error?.graphQLErrors.find(
    e => e.extensions.code === "TIMEOUT_ERROR",
  );

  const initialReportTypes = buildInitialReportTypes({
    initialReportTypes: [SDE_DATABASE_REPORT],
    account,
    submissionTypeNames: [INITIAL_SUBMISSION_TYPE_REPORTS.SI_SD_REPORT],
  });

  const actions = <ExportDataButton initialReportTypes={initialReportTypes} />;

  return (
    <FullWidthTable
      columns={initialColumns}
      previousData={previousData?.queryTableData.data}
      currentData={data?.queryTableData.data ?? []}
      loadingDetails={loadingDetails}
      manualPaginationConfig={{
        ...manualPaginationConfig,
        pageCount: data?.queryTableData.pageInfo.totalPages ?? 1,
      }}
      initialState={initialTableState}
      filterable={{
        filterConfiguration: columnConfigResponse.getTableConfig
          .data as Array<Attribute>,
        search,
      }}
      columnSettingProps={{
        columnConfiguration: columnConfigResponse.getTableConfig.data,
        columnDefinitions: tanstackColumnDefinitions,
      }}
      timeoutError={timeoutError}
      queryDescription={queryDescription}
      currentView={currentView}
      updateQueryDescription={updateQueryDescription}
      savedViews={savedViewsResponse?.account?.savedViews}
      refetchSavedViews={refetchSavedViews}
      refetchTable={refetchTable}
      actions={actions}
      clientSideColumnConfig={columnConfigResponse.getTableConfig.data}
      dataSourceComputationsAffectingQuery={
        data?.queryTableData.dataSourceComputationsAffectingQuery
      }
      invalidQueryConfig={data?.queryTableData.invalidQueryConfig ?? null}
      rowIdField={`${TABLE_NAMES.TASKS}.id`}
      totalCount={data?.queryTableData.pageInfo.totalCount ?? 0}
      actionBarConfig={actionBarConfig}
    />
  );
};

export default TasksTable;
